import { Component, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChatService } from '../../../services/chat/chat.service';
import { IdentityService } from '../../../services/identity/identity.service';
import { ToasterService } from '../../../services/toaster/toaster.service';
import { PortraitService } from '../../../services/portraits/portrait.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-user-profile-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './user-profile-modal.html',
  styleUrl: './user-profile-modal.scss',
})
export class UserProfileModal implements OnInit {
  private chatService = inject(ChatService);
  private identityService = inject(IdentityService);
  private toaster = inject(ToasterService);
  private portraitService = inject(PortraitService);
  private http = inject(HttpClient);

  public user = this.chatService.selectedUserForModal;
  public portraits = this.portraitService.portraits;

  public isMe = computed(() => {
    const u = this.user();
    if (!u) return false;

    const claims = this.identityService.getClaims();
    return claims && u.userName === claims.unique_name;
  });

  public isParentLobbyOwner = computed(() => {
    const arena = this.chatService.arena();
    if (!arena) return false;

    const u = this.user();
    if (!u) return false;

    const userId = u.id || u.guid || u.userId;
    const parentOwnerId = arena.parentGuid == null ? arena.ownerId : arena.parentOwnerId;

    if (!parentOwnerId) return false;

    return (userId && userId === parentOwnerId) || (u.userName && u.userName === parentOwnerId);
  });

  public hasActiveIcon = computed(() => {
    const u = this.user();
    if (!u || !u.userIcons) return false;
    return u.userIcons.some((i: any) => i.active);
  });

  ngOnInit() {
    this.portraitService.getAll();
  }

  getPortraitUrl(portraitId: any): string {
    const portraits = this.portraits();
    if (portraits.length > 0) {
      const portrait = portraits.find(p => p.id == portraitId);
      if (portrait) {
        return portrait.fileName;
      }
    }
    return "bagfacepak/2.jpg";
  }

  getStatusText(status: any) {
    switch (status) {
      case 1: return 'online';
      case 2: return 'away';
      case 3: return 'busy';
      case 4: return 'ingame';
      case 5: return 'ready';
      default: return 'offline';
    }
  }

  getStatusColor(status: any) {
    switch (status) {
      case 1: return '#198754';
      case 2: return '#dc3545';
      case 3: return '#ffc107';
      case 4: return '#0d6efd';
      case 5: return '#ffc107';
      default: return '#6c757d';
    }
  }

  selectIcon(icon: any | null) {
    if (!this.isMe()) return;

    const updatedUser = { ...this.user() };

    // Update active status on local copy of icons
    if (updatedUser.userIcons && Array.isArray(updatedUser.userIcons)) {
      updatedUser.userIcons = updatedUser.userIcons.map((i: any) => ({
        ...i,
        active: icon ? i.id === icon.id : false
      }));
    }

    // Update activeIcon property
    updatedUser.activeIcon = icon;

    // Save back to the signal
    this.chatService.selectedUserForModal.set(updatedUser);

    // Propagate changes to ChatService.users signal so that player-panel immediately updates
    const usersList = this.chatService.users();
    const index = usersList.findIndex(u => u.userName === updatedUser.userName);
    if (index !== -1) {
      usersList[index] = updatedUser;
      this.chatService.users.set([...usersList]);
    }

    if (icon) {
      this.toaster.success(`Active badge changed to: ${icon.description}`);
    } else {
      this.toaster.success("Cleared active badge");
    }

    const id = icon ? icon.id : 0;
    this.http.get(`${environment.platformUrl}api/icon/update/${id}`).subscribe({
      next: () => {
        this.chatService.updateUser();
      },
      error: (err) => {
        console.error('Failed to update active icon', err);
        this.toaster.danger('Failed to update active icon on server.');
      }
    });
  }
}
